import React, { FC, useEffect } from 'react';
import { Trans } from '@lingui/macro';

import { Envelope, Megaphone, Share } from '@rover/icons';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useGateValue } from '@rover/rsdk/src/modules/Statsig/';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import NavBarItem from '../NavBarItem';

import { PromoteYourBusinessHeaderClick, ReferralCTAClick } from './analyticsEvents';

const ReferLink: FC = (): JSX.Element | null => {
  const rolloutAmbassadorOwnerEntryPointsUplifts = useGateValue(
    'rollout_ambassador_owner_entry_points_uplifts'
  );

  const { isAuthenticated, isActiveServiceProvider } = useCurrentUserShim();
  const { current: cc } = useCountryConfigurationShim();
  const rolloutSitterPromotionEntryPoints = useFlag(FLAGS.ROLLOUT_SITTER_PROMOTION_M0_ENTRY_POINTS);

  const formattedReferralGiftcardAmount = cc?.formattedReferralGiftcardAmount ?? '$20';
  const formattedReferralOwnerToOwnerReward = cc?.formattedReferralOwnerToOwnerReward ?? '$20';

  const showOwnerEntrypoint = !isActiveServiceProvider && cc?.supportsReferral;
  const showSitterEntrypoint = rolloutSitterPromotionEntryPoints && isActiveServiceProvider;

  const shouldFireLoadEvent = showOwnerEntrypoint || showSitterEntrypoint;

  let referralCTA: string;

  if (showSitterEntrypoint) {
    referralCTA = 'ssr-sitter-promotion-header';
  } else if (showOwnerEntrypoint) {
    referralCTA = 'ssr-owner-referral-header';
  }

  useEffect(() => {
    if (shouldFireLoadEvent) {
      fireDataLayerEvent({
        event: 'referral-cta-view',
        referral_cta: referralCTA,
      });
    }
  }, []);

  if (!isAuthenticated) return null;

  if (showOwnerEntrypoint) {
    if (rolloutAmbassadorOwnerEntryPointsUplifts) {
      return (
        <NavBarItem
          display={['none', 'none', 'block']}
          href="/refer/?source=header_ssr"
          icon={Envelope}
          onClick={() => {
            emitAnalyticsEvent(new ReferralCTAClick({ referralCTA }, { referralCTA }));
          }}
          data-testid="ambassador-refer-link"
        >
          <Trans>Invite a Friend</Trans>
        </NavBarItem>
      );
    }

    return (
      <NavBarItem
        display={['none', 'none', 'block']}
        href="/refer/?source=header_ssr"
        icon={Share}
        data-testid="refer-link"
      >
        <Trans>
          Give {formattedReferralGiftcardAmount}, Get {formattedReferralOwnerToOwnerReward}
        </Trans>
      </NavBarItem>
    );
  }

  if (showSitterEntrypoint) {
    return (
      <NavBarItem
        display={['none', 'none', 'block']}
        href="/provider-profile/promote/"
        icon={Megaphone}
        onClick={() => {
          emitAnalyticsEvent(new PromoteYourBusinessHeaderClick());
        }}
        data-testid="ssp-refer-link"
      >
        <Trans>Promote your profile</Trans>
      </NavBarItem>
    );
  }
  return null;
};

export default ReferLink;
